import { BrowserProvider } from "ethers";
import React, { useState } from "react";
import { API_URL } from '../config';
import { useAuth } from "../contexts/AuthContext";
import { magic } from "../lib/magic";
import { placeParlayOnchain } from "../lib/onchain";
import LoadingDots from "./LoadingDots";
import LoginModal from "./LoginModal";

interface ParlayLeg {
  id?: number; // Database leg ID
  eventTitle: string;
  marketQuestion: string;
  outcome: "yes" | "no";
  outcomePrice: number;
  endDate?: string;
  marketIcon?: string;
  marketId?: string; // <-- should be Polymarket/ingest market id
}

interface ParlayStats {
  total_amount: number;
  total_odds: number;
  expected_payout: number;
  legs_count: number;
}

interface ParlayConfirmationProps {
  legs: ParlayLeg[];
  stats: ParlayStats;
  onConfirm: () => void;
  onAddMoreLegs: () => void;
  onCancelParlay?: () => void;
  onRefreshParlay?: () => void;
  onRemoveLegs?: (legIds: string[]) => void;
}

const ParlayConfirmation: React.FC<ParlayConfirmationProps> = ({
  legs,
  stats,
  onConfirm,
  onAddMoreLegs,
  onCancelParlay,
  onRefreshParlay,
  onRemoveLegs
}) => {
  const { isAuthenticated, user } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  // const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [selectedLegIds, setSelectedLegIds] = useState<Set<string>>(new Set());
  const [removingLegs, setRemovingLegs] = useState(false);

  // New UI state for on-chain flow
  const [isQuoting, setIsQuoting] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleLegSelection = (leg: ParlayLeg, index: number) => {
    const legId = leg.marketId || `${leg.marketQuestion}_${index}`;
    setSelectedLegIds(prev => {
      const next = new Set(prev);
      next.has(legId) ? next.delete(legId) : next.add(legId);
      return next;
    });
  };

  // --- helpers --------------------------------------------------------------

  async function getWalletAddressFallback(): Promise<string | undefined> {
    try {
      const provider = new BrowserProvider(magic.rpcProvider as any);
      const signer = await provider.getSigner();
      return await signer.getAddress();
    } catch {
      return undefined;
    }
  }

  function toEngineLegs(source: ParlayLeg[]) {
    // Flask expects: [{ ingest_market_id, outcome_label }]
    // Your legs carry marketId + outcome: 'yes'|'no'
    return source.map(l => ({
      ingest_market_id: l.marketId!,                            // must exist
      outcome_label: l.outcome.toLowerCase() === "yes" ? "Yes" : "No"
    }));
  }

  async function requestQuote(stake: number) {
    let walletAddr = user?.walletAddress || (await getWalletAddressFallback());
    
    // If gas sponsorship is available, use smart account address for quotes
    if (user?.gasSponsored && user?.smartAccountAddress) {
      walletAddr = user.smartAccountAddress;
      console.log('🚀 Using smart account address for quote:', walletAddr);
    }
    
    if (!walletAddr) throw new Error("No wallet address found.");
    if (!user?.issuer) throw new Error("Missing user issuer for quoting.");

    const payload = {
      issuer: user.issuer,
      smart_contract_wallet_address: walletAddr,
      stake,
      legs: toEngineLegs(legs)
    };

    const res = await fetch(`${API_URL}/quote`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/quote failed: ${text}`);
    }
    return res.json(); // includes quoteWire
  }

  // --- remove legs using quote endpoint --------------------------------------

  const handleRemoveLegs = async () => {
    if (selectedLegIds.size === 0) return;
    
    if (selectedLegIds.size === legs.length) {
      // All legs selected - cancel parlay
      onCancelParlay ? onCancelParlay() : onAddMoreLegs();
      return;
    }

    // Call parent to handle leg removal and requoting
    if (onRemoveLegs) {
      const legIdsArray = Array.from(selectedLegIds);
      onRemoveLegs(legIdsArray);
      setSelectedLegIds(new Set());
    }
  };

  // --- MAIN: confirm → quote → approve → place (on-chain) ------------------

  const handleConfirm = async () => {
  setErrorMsg(null);
  setTxHash(null);

  // 1) Require auth (but no balance gating)
  if (!isAuthenticated) {
    sessionStorage.setItem("pendingParlayData", JSON.stringify({ legs, stats, ts: Date.now() }));
    sessionStorage.setItem("postAuthRedirectUrl", window.location.pathname + window.location.search);
    sessionStorage.setItem("authContext", "parlay-confirmation");
    setIsLoginModalOpen(true);
    return;
  }

  try {
    // 2) Get fresh quote
    setIsQuoting(true);
    console.log(API_URL)
    const quoteResp = await requestQuote(stats.total_amount);
    console.log("Received quote response:", quoteResp);
    setIsQuoting(false);

    // Expecting backend fields we actually return: quote_struct + houseSig
    if (!quoteResp?.quote_struct || !quoteResp?.houseSig) {
      throw new Error("Backend did not return quote_struct/houseSig.");
    }
    
    // Log quote expiry details for debugging
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = quoteResp.quote_struct.expiry - currentTimestamp;
    console.log("Quote expiry details:", {
      expires_at: quoteResp.expires_at,
      expiry_unix: quoteResp.quote_struct.expiry,
      current_time_unix: currentTimestamp,
      time_until_expiry: timeUntilExpiry
    });
    
    // Declare placed variable outside conditional blocks
    let placed: any = null;
    let finalQuoteResp = quoteResp;
    
    // Check if quote is about to expire (less than 30 seconds remaining)
    if (timeUntilExpiry < 30) {
      console.log("Quote is about to expire, getting fresh quote...");
      // Get a fresh quote before proceeding
      const freshQuoteResp = await requestQuote(stats.total_amount);
      console.log("Received fresh quote response:", freshQuoteResp);
      finalQuoteResp = freshQuoteResp;
      
      // Use the fresh quote for on-chain submission
      setIsPlacing(true);
      try {
        placed = await placeParlayOnchain(freshQuoteResp);
        if (placed?.txHash) setTxHash(placed.txHash);
      } catch (blockchainError: any) {
        const errorMsg = blockchainError?.message || "Transaction failed";
        if (errorMsg.includes("user rejected") || errorMsg.includes("User denied")) {
          throw new Error("Transaction cancelled. You can try again.");
        } else if (errorMsg.includes("insufficient")) {
          throw new Error("Insufficient balance for transaction");
        } else if (errorMsg.includes("quote used")) {
          throw new Error("Quote already used. Please request a new quote.");
        } else if (errorMsg.includes("execution reverted")) {
          throw new Error("Transaction failed. Please try again.");
        } else if (blockchainError.name === 'GasSponsorshipError' || errorMsg.includes("Gas sponsorship failed")) {
          throw new Error("Gas sponsorship failed after multiple attempts. Please try again.");
        }
        throw blockchainError;
      }
    } else {
      // Quote is still valid, proceed with current quote
      // 3) On-chain: approve if needed + place
      setIsPlacing(true);
      try {
        placed = await placeParlayOnchain(quoteResp);
        if (placed?.txHash) setTxHash(placed.txHash);
      } catch (blockchainError: any) {
        const errorMsg = blockchainError?.message || "Transaction failed";
        if (errorMsg.includes("user rejected") || errorMsg.includes("User denied")) {
          throw new Error("Transaction cancelled. You can try again.");
        } else if (errorMsg.includes("insufficient")) {
          throw new Error("Insufficient balance for transaction");
        } else if (errorMsg.includes("quote used")) {
          throw new Error("Quote already used. Please request a new quote.");
        } else if (errorMsg.includes("execution reverted")) {
          throw new Error("Transaction failed. Please try again.");
        } else if (blockchainError.name === 'GasSponsorshipError' || errorMsg.includes("Gas sponsorship failed")) {
          throw new Error("Gas sponsorship failed after multiple attempts. Please try again.");
        }
        throw blockchainError;
      }
    }

    // 4) Tell backend so it can record an off-chain ticket (until your event listener is live)
    await fetch(`${API_URL}/tickets/record`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        quoteId: finalQuoteResp.quoteId,
        txHash: placed?.txHash || null
      })
    });

    // 5) Success
    onConfirm();
  } catch (e: any) {
    console.error(e);
    setErrorMsg(e?.message || "Failed to place parlay.");
  } finally {
    setIsQuoting(false);
    setIsPlacing(false);
  }
};

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    // after login, just retry the same flow
    handleConfirm();
  };

  const handleDepositComplete = async () => {
    // setIsDepositModalOpen(false);
    // after deposit, retry flow
    handleConfirm();
  };

  const handleCancelParlay = async () => {
    // With client-side state management, just trigger parent to clear state
    onCancelParlay ? onCancelParlay() : onAddMoreLegs();
  };

  // --- UI helpers -----------------------------------------------------------

  const formatDate = (dateString?: string) => {
    if (!dateString) return "TBD";
    try {
      const d = new Date(dateString);
      const now = new Date();
      const isNextYear = d.getFullYear() > now.getFullYear();
      
      return d.toLocaleDateString("en-US", { 
        month: "2-digit", 
        day: "2-digit",
        ...(isNextYear && { year: 'numeric' })
      });
    } catch {
      return "TBD";
    }
  };

  const formatPrice = (price: number) => `${(price * 100).toFixed(1)}¢`;

  const isBusy = isQuoting || isPlacing || removingLegs;

  // --- render ---------------------------------------------------------------

  return (
    <div className="parlay-confirmation">
      <div className="confirmation-header">
        <h2>Review Your Parlay</h2>
        {isQuoting && <div className="status">Getting quote… <LoadingDots /></div>}
        {isPlacing && <div className="status">Submitting on-chain… <LoadingDots /></div>}
        {txHash && (
          <div className="status">
            Submitted:{" "}
            <a href={`https://sepolia.basescan.org/tx/${txHash}`} target="_blank" rel="noreferrer">
              {txHash.slice(0, 10)}…
            </a>
          </div>
        )}
        {errorMsg && <div className="error">{errorMsg}</div>}
      </div>

      <div className="confirmation-legs">
        {legs.map((leg, index) => {
          const legId = leg.marketId || `${leg.marketQuestion}_${index}`;
          return (
            <div key={index} className="confirmation-leg">
              <div className="leg-selector">
                <input
                  type="checkbox"
                  checked={selectedLegIds.has(legId)}
                  onChange={() => handleLegSelection(leg, index)}
                  className="leg-checkbox"
                  disabled={isBusy}
                />
              </div>
              <div className="leg-icon">
                {leg.marketIcon && (
                  <img src={leg.marketIcon} alt={leg.marketQuestion} className="market-icon-img" />
                )}
              </div>
              <div className="leg-outcome">
                <span className={`outcome-badge ${leg.outcome}`}>{leg.outcome.toUpperCase()}</span>
              </div>
              <div className="leg-details">
                <div className="leg-question">{leg.marketQuestion}</div>
                <div className="leg-meta">
                  <span className="leg-price">{formatPrice(leg.outcomePrice)}</span>
                  <span className="leg-date">Resolution Date: {formatDate(leg.endDate)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="confirmation-totals">
        <div className="total-item">
          <span className="total-label">Total Parlay Amount:</span>
          <span className="total-value">${stats.total_amount.toFixed(2)}</span>
        </div>
        <div className="total-item">
          <span className="total-label">Odds:</span>
          <span className="total-value">{stats.total_odds.toFixed(2)}x</span>
        </div>
        <div className="total-item">
          <span className="total-label">Expected Payout:</span>
          <span className="total-value">${stats.expected_payout.toFixed(2)}</span>
        </div>
        <div className="total-item">
          <span className="total-label">Settlement Date:</span>
          <span className="total-value">
            {legs.length > 0
              ? (() => {
                  const valid = legs
                    .map(l => (l.endDate ? new Date(l.endDate).getTime() : 0))
                    .filter(t => t > 0);
                  if (!valid.length) return "TBD";
                  const latest = Math.max(...valid);
                  return formatDate(new Date(latest).toISOString());
                })()
              : "TBD"}
          </span>
        </div>
      </div>

      <div className="confirmation-actions">
        <button
          className="cancel-parlay-btn"
          onClick={selectedLegIds.size > 0 ? handleRemoveLegs : handleCancelParlay}
          disabled={isBusy}
        >
          {removingLegs ? (
            <LoadingDots className="button-loading" />
          ) : selectedLegIds.size > 0 ? (
            `Remove Parlay Leg${selectedLegIds.size > 1 ? "s" : ""}`
          ) : (
            "Cancel Parlay"
          )}
        </button>
        <button className="add-more-legs-btn" onClick={onAddMoreLegs} disabled={isBusy}>
          Add More Legs
        </button>
        <button className="confirm-btn" onClick={handleConfirm} disabled={isBusy}>
          {isBusy ? "Processing…" : "Confirm & Place Parlay"}
        </button>
      </div>

      <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginSuccess} />
      {/*<DepositModal isOpen={isDepositModalOpen} onClose={handleDepositComplete} insufficientFunds={true} />*/}
    </div>
  );
};

export default ParlayConfirmation;
